import { useCallback, useMemo, useState } from 'react';

import { isVisible } from './isVisible';

interface Options {
    threshold?: number;
}

interface HookResult {
    hasEnteredView: boolean;
    ref: (observedElement: HTMLElement | null) => void;
}

/**
 * A custom React hook that detects when an element has entered the viewport.
 *
 * This hook uses the Intersection Observer API to efficiently track when an element
 * becomes visible in the viewport.
 *
 * @param {Options} [options] - Configuration options for the Intersection Observer.
 * @param {number} [options.threshold] - A number between 0 and 1 indicating the percentage
 *                                       of the target's visibility at which to trigger
 *                                       the callback.
 * @returns {HookResult} An object containing:
 *                       - hasEnteredView: A boolean indicating if the element has entered the view.
 *                       - ref: A callback ref to attach to the observed element.
 *
 * @example
 * // Basic usage
 * function VisibilityTracker() {
 *   const { hasEnteredView, ref } = useHasEnteredView();
 *
 *   return (
 *     <div ref={ref}>
 *       {hasEnteredView ? 'I am visible!' : 'I am not yet visible.'}
 *     </div>
 *   );
 * }
 *
 * @example
 * // Usage with threshold option
 * function LazyLoadedImage({ src, alt }) {
 *   const { hasEnteredView, ref } = useHasEnteredView({ threshold: 0.5 });
 *
 *   return (
 *     <div ref={ref}>
 *       {hasEnteredView ? (
 *         <img src={src} alt={alt} />
 *       ) : (
 *         <div>Loading...</div>
 *       )}
 *     </div>
 *   );
 * }
 *
 * @example
 * // Usage in a list of items
 * function ItemList({ items }) {
 *   return (
 *     <ul>
 *       {items.map(item => (
 *         <ListItem key={item.id} item={item} />
 *       ))}
 *     </ul>
 *   );
 * }
 *
 * function ListItem({ item }) {
 *   const { hasEnteredView, ref } = useHasEnteredView();
 *
 *   return (
 *     <li ref={ref}>
 *       {hasEnteredView ? (
 *         <ItemDetails item={item} />
 *       ) : (
 *         <ItemPlaceholder />
 *       )}
 *     </li>
 *   );
 * }
 */
export function useHasEnteredView(options?: Options): HookResult {
    const [hasEnteredView, setHasEnteredView] = useState(false);

    const observer = useMemo(() => {
        const onThresholdCrossed = ([entry]: IntersectionObserverEntry[]): void => {
            if (isVisible(entry.target)) {
                setHasEnteredView(true);
            }
        };

        return new IntersectionObserver(onThresholdCrossed, options);
    }, [options]);

    const callbackRef = useCallback(
        (observedElement: HTMLElement | null) => {
            if (observedElement && isVisible(observedElement)) {
                setHasEnteredView(true);
                return;
            }

            if (observedElement) {
                observer.observe(observedElement);
                return;
            }

            observer.disconnect();
        },
        [observer],
    );

    return {
        hasEnteredView,
        ref: callbackRef,
    };
}
